import Grid from "@mui/material/Grid";
import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";

import {
  BridgeSVG,
  DashboardSVG,
  GatewayFullLogoSVG,
  LogoutSVG,
  MagnifyingGlassSVG,
  ProfileSVG,
  WalletTransparentSVG,
} from "../../assets";
import { Button, Searchbar } from "../../components";
import { BACKEND_AI_AGENT_URL } from "../../config";
import { useWallet } from "../../contexts";
import { useEvmWalletActions, useNetworks } from "../../hooks";
import { useWindowDimensions } from "../../hooks/UI";
import {
  GatewayScore,
  Price,
  ProjectConfig,
  Volume,
} from "../../pages/AIAnalyticsDashboard/types";
import { shortAddress, shortAddressMobile } from "../../utils/utils";
import { CurrentChainButton } from "../CurrentChainButton/CurrentChainButton";
import "./Navbar.scss";

type OpenType = "MY_PROFILE" | "MENU";

export interface AllProjects {
  project: ProjectConfig;
  volume: Volume;
  price: Price;
  gatewayScore: GatewayScore;
}
export interface TrendingProjects {
  project: ProjectConfig;
  gatewayScore: GatewayScore;
  price: Price;
}

export const Navbar = () => {
  const [isOpen, setOpen] = useState<OpenType | null>(null);
  const { networks } = useNetworks();
  const [searchValue, setSearchValue] = useState("");

  const [allProjects, setAllProjects] = useState<AllProjects[]>([]);

  useEffect(() => {
    const fetchTrendingData = async () => {
      try {
        const response = await fetch(
          `${BACKEND_AI_AGENT_URL}/homepage-projects`,
        );
        const data: {
          allProjects: AllProjects[];
          trendingProjects: TrendingProjects[];
        } = await response.json();

        setAllProjects(data.allProjects);
      } catch (error) {
        console.error("Failed to fetch trending narratives:", error);
      }
    };

    fetchTrendingData();
  }, []);

  return (
    <div className="relative z-100">
      <div
        className={`navbar__wrapper ${
          isOpen ? "navbar__wrapper--mobile-menu" : ""
        } `}
      >
        {Object.values(networks).some((network) => network.isTestnet) && (
          <Grid
            className="navbar__testnet-info"
            container
            justifyContent="center"
            alignItems="center"
          >
            <Grid className="navbar__testnet-info-item" item>
              {Object.values(networks)
                .filter((network) => network.isTestnet)
                .map((network) => network.networkName)
                .join(" AND ")}{" "}
              TESTNET
            </Grid>
          </Grid>
        )}
        <div className="navbar__bar">
          <div className="navbar__outer-container">
            <div className="navbar__inner-container">
              <Link to="/" aria-label="Go to the main page">
                <div className="navbar__logo">
                  <GatewayFullLogoSVG />
                </div>
              </Link>

              <div className="navbar__search-bar-wrapper">
                <MagnifyingGlassSVG className="w-5 h-5 fill-[#007DFF] md:hidden" />

                <div className="navbar__search-bar-container">
                  <Searchbar
                    data={allProjects}
                    value={searchValue}
                    onChange={setSearchValue}
                  />
                </div>
              </div>

              {!isOpen && <NavbarWallet setOpen={setOpen} />}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const NavbarWallet = ({ setOpen }: { setOpen: (val: OpenType) => void }) => {
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const { networks } = useNetworks();
  const { openWalletModal } = useWallet();
  const { isLargeScreen } = useWindowDimensions();
  const { disconnectWallets } = useEvmWalletActions();

  const connectedAddress =
    networks.cosmos.address ||
    networks.evm.address ||
    networks.aptos.address ||
    networks.solana.address;

  const handleProfileClick = (e: React.MouseEvent) => {
    e.stopPropagation();

    if (!connectedAddress) {
      openWalletModal();
      return;
    }

    if (isLargeScreen) {
      setOpen("MY_PROFILE");
    } else {
      setIsDropdownOpen((prev) => !prev);
    }
  };

  const hasMoreThanOneConnectedWallet =
    Object.values(networks).filter((network) => network.address).length > 1;

  return (
    <>
      <div
        className={`navbar__wallet-mobile navbar__wallet ${
          connectedAddress ? "navbar__wallet-connected" : ""
        }`}
      >
        <Button
          className={`navbar__wallet-connect-button`}
          onClick={openWalletModal}
          variant="TRANSPARENT"
        >
          <span className="navbar__wallet-connect-button-content">
            {connectedAddress ? (
              <>
                <CurrentChainButton />
                {!hasMoreThanOneConnectedWallet && (
                  <span>{shortAddressMobile(connectedAddress)}</span>
                )}
              </>
            ) : (
              <WalletTransparentSVG width={18} height={18} />
            )}
          </span>
        </Button>
        <div className="navbar__wallet-divider"></div>
        <Button
          onClick={handleProfileClick}
          className="navbar__wallet-connect-button-profile"
          variant="TRANSPARENT"
        >
          <ProfileSVG width={20} height={20} />
        </Button>
      </div>

      <div
        className={`navbar__wallet-desktop navbar__wallet ${
          connectedAddress ? "navbar__wallet-connected" : ""
        }`}
      >
        <Button
          className={`navbar__wallet-connect-button`}
          onClick={openWalletModal}
          variant="TRANSPARENT"
        >
          <span className="navbar__wallet-connect-button-content">
            {connectedAddress ? (
              <>
                <CurrentChainButton />
                <span>{shortAddress(connectedAddress)}</span>
              </>
            ) : (
              "Connect wallet"
            )}
          </span>
        </Button>
        <div className="navbar__wallet-divider"></div>
        <Button
          onClick={handleProfileClick}
          className="navbar__wallet-connect-button-profile"
          aria-label="Profile options"
          variant="TRANSPARENT"
        >
          <ProfileSVG width={28} height={28} />
          <>
            {isDropdownOpen && (
              <div className="navbar__wallet-dropdown-menu">
                <Link
                  to="/bridge"
                  className="navbar__wallet-dropdown-menu-item"
                >
                  Bridge
                  <BridgeSVG />
                </Link>
                <Link
                  to="/dashboard"
                  className="navbar__wallet-dropdown-menu-item"
                >
                  Dashboard
                  <DashboardSVG />
                </Link>
                <div
                  className="navbar__wallet-dropdown-menu-item"
                  onClick={disconnectWallets}
                >
                  Log out
                  <LogoutSVG />
                </div>
              </div>
            )}
          </>
        </Button>
      </div>
    </>
  );
};
